import type { WizardQuestion, Tone, BenefitKey } from './types';

// Position types treated as "shift coverage" roles. Mirrors the backend
// `position_type` enum.
const TEMP_POSITION_TYPES = new Set(['temp', 'longtermtemp']);

// Matches `DideWriter::Answers::TEXT_MAX` on the server.
const TEXTAREA_MAX_LENGTH = 1000;

// Every question is optional: the writer works from the job form fields and the
// extra-info note, and the answers only add detail.

const Q1_TEXTAREA: WizardQuestion = {
  id: 'q1',
  type: 'textarea',
  label: 'What\'s the #1 reason someone loves working at your practice?',
  helper: 'One or two sentences. We\'ll weave this into the opening.',
  placeholder: 'e.g., Mentorship from senior hygienists and real growth paths.',
  maxLength: TEXTAREA_MAX_LENGTH,
};

const Q2_RESPONSIBILITIES: WizardQuestion = {
  id: 'q2',
  type: 'chips',
  label: 'What will this person do day-to-day?',
  helper: 'Add a few responsibilities. Press enter or comma to add each.',
  maxItems: 10,
  suggestions: [
    'Assist chairside during procedures',
    'Sterilize instruments',
    'Take digital x-rays',
    'Manage patient records',
    'Greet and check in patients',
    'Process payments and insurance',
  ],
};

// Temp / longterm-temp variant: shift coverage matters more than career arc.
// Kept as `chips` so the answer shape (`string[]`) stays compatible with the
// permitted-params contract on the backend (`q2: []`).
const Q2_COVERAGE: WizardQuestion = {
  id: 'q2',
  type: 'chips',
  label: 'What shifts or coverage do you need?',
  helper: 'List the days, hours, or weeks you need filled. Press enter or comma to add each.',
  maxItems: 10,
  suggestions: [
    'Tue / Thu 8am-5pm',
    'Mon-Fri 9am-3pm',
    'Alternating Saturdays',
    '2-week maternity coverage',
    'On-call hygiene',
  ],
};

const Q3_SKILLS: WizardQuestion = {
  id: 'q3',
  type: 'chips',
  label: 'Any must-have skills or software?',
  helper: 'e.g., Dentrix, Open Dental, intra-oral scanning.',
  maxItems: 12,
};

const Q4_TONE: WizardQuestion = {
  id: 'q4',
  type: 'tone',
  label: 'What tone should we strike?',
  helper: 'We\'ll match this across the whole post.',
};

const Q5_BENEFITS: WizardQuestion = {
  id: 'q5',
  type: 'benefits',
  label: 'Call out any stand-out benefits?',
  helper: 'Only pick benefits your practice actually offers.',
};

const Q6_UNIQUE: WizardQuestion = {
  id: 'q6',
  type: 'textarea',
  label: 'Anything unique about your practice?',
  helper: 'Parking, flexible schedule, specific philosophy, etc.',
  placeholder: 'e.g., Two blocks from the train, and we close at noon on Fridays.',
  maxLength: TEXTAREA_MAX_LENGTH,
};

// Default order (fulltime / parttime). Exported for back-compat with existing
// callers and for tests. New code should call `questionsForPositionType()` so
// the `temp` / `longtermtemp` variant flows through automatically. The tone
// question is asked on the first screen, next to the extra-info box, so the
// optional step-by-step questions use `optionalQuestionsForPositionType()`.
export const DIDE_WRITER_QUESTIONS: WizardQuestion[] = [
  Q1_TEXTAREA,
  Q2_RESPONSIBILITIES,
  Q3_SKILLS,
  Q4_TONE,
  Q5_BENEFITS,
  Q6_UNIQUE,
];

// Returns the wizard schema appropriate for the given `position_type`.
// Temp / longterm temp swap Q2 for "What shifts/coverage do you need?" and
// drop Q5 (benefits feel less relevant for coverage gigs).
export const questionsForPositionType = (
  positionType?: string | null
): WizardQuestion[] => {
  if (positionType && TEMP_POSITION_TYPES.has(positionType)) {
    return [Q1_TEXTAREA, Q2_COVERAGE, Q3_SKILLS, Q4_TONE, Q6_UNIQUE];
  }
  return DIDE_WRITER_QUESTIONS;
};

// The step-by-step optional questions: everything except tone.
export const optionalQuestionsForPositionType = (
  positionType?: string | null
): WizardQuestion[] => questionsForPositionType(positionType).filter(q => q.id !== 'q4');

export const TONE_QUESTION: WizardQuestion = Q4_TONE;

export const TONE_OPTIONS: Array<{ value: Tone; label: string; hint: string }> = [
  { value: 'warm', label: 'Warm', hint: 'People-first, approachable' },
  { value: 'professional', label: 'Professional', hint: 'Balanced, corporate-friendly' },
  { value: 'energetic', label: 'Energetic', hint: 'Upbeat, punchy' },
  { value: 'concise', label: 'Concise', hint: 'Straight to the point' },
];

// Map matches `AiJobDescriptionWriterService::ALLOWED_BENEFIT_KEYS` 1:1 and the
// job column name `benefit_<key>`. Keep in sync with backend.
export const BENEFIT_OPTIONS: Array<{ value: BenefitKey; label: string; column: string }> = [
  { value: 'medical', label: 'Medical', column: 'benefit_medical' },
  { value: 'dental', label: 'Dental', column: 'benefit_dental' },
  { value: 'vision', label: 'Vision', column: 'benefit_vision' },
  { value: '401k', label: '401(k)', column: 'benefit_401k' },
  { value: 'pto', label: 'PTO', column: 'benefit_pto' },
  { value: 'bonuses', label: 'Bonuses', column: 'benefit_bonuses' },
];

export const benefitKeyToColumn = (key: BenefitKey): string =>
  BENEFIT_OPTIONS.find(opt => opt.value === key)?.column ?? `benefit_${key}`;

// Answers are required iff (required question AND value blank). Return an array
// of question ids that still need to be answered. Pass the question schema in
// explicitly so callers can validate against the position-type-specific
// variant (default falls back to the fulltime/parttime schema).
export const missingRequiredAnswers = (
  answers: Partial<Record<string, unknown>>,
  questions: WizardQuestion[] = DIDE_WRITER_QUESTIONS
): string[] => {
  const missing: string[] = [];
  for (const q of questions) {
    if (!q.required) continue;
    const v = answers[q.id];
    if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) {
      missing.push(q.id);
    }
  }
  return missing;
};
